/* eslint-disable prefer-const,react/require-default-props,max-len */
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Collapse,
  FormControlLabel,
  Grid2,
  MenuItem,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { FaCarSide, FaTruck, FaVanShuttle } from 'react-icons/fa6';
import {
  FILTER_GROUPS,
  FILTER_SKELETON,
  FUEL_TYPE,
  TRANSMISSION_TYPE,
  useFilters,
} from '@plugins/vehiclefleetapp';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'next-i18next';
import { FiArrowDownCircle, FiArrowUpCircle } from 'react-icons/fi';
import orderBy from 'lodash/orderBy';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import Select from '@components/atoms/Select';
import BasicFiltersGroup, { FormSlider } from './BasicFiltersGroup';
import { QUERY_CAR_SEARCH_FILTER_DATA } from './graphQl';
import FeaturesFilterGroup from './FeaturesFilterGroup';
import { transformBrandTitle } from '../CarBrandTitle';
import { dimensions } from '@/theme';

export default function SearchAndFilterBar(props) {
  const {
    id,
    filters,
    onChange,
    onReset,
  } = props;
  const [isDetailedSearchOpen, setDetailedSearchOpen] = useState(false);
  const { t } = useTranslation();

  const theme = useTheme();
  const [expandedGroup, setExpandedGroup] = useState('basic');
  const [cachedTotalCount, setTotalCount] = useState(0);
  const [animTimeout, setAnimTimeout] = useState(0);

  const {
    error,
    data,
    refetch,
  } = useQuery(QUERY_CAR_SEARCH_FILTER_DATA, {
    variables: {
      ...filters,
    },
  });

  if (error) {
    throw error;
  }

  const {
    // eslint-disable-next-line no-unused-vars
    isLoading,
    filterData,
    queryParams,
    changeHandlers,
  } = useFilters(FILTER_SKELETON, {
    data,
    refetch,
    onChange,
  });

  const {
    totalCount,
  } = data?.carSearchResultCount ?? {
    totalCount: cachedTotalCount,
  };

  useEffect(() => {
    setAnimTimeout(undefined);
  }, [setAnimTimeout]);
  useEffect(() => {
    setTotalCount(totalCount);
  }, [totalCount]);

  const handleShowGroup = async (group) => {
    if (expandedGroup) {
      setExpandedGroup(null);

      if (expandedGroup === group) {
        return;
      }

      await new Promise((resolve) => {
        setTimeout(resolve, 500);
      });
    }

    setExpandedGroup(group);
  };
  const handleRefetch = () => {
    // refetch(queryParams);
    const yOffSet = -180;
    const element = document.getElementById(id);
    const y = element.getBoundingClientRect().top + window.pageYOffset + yOffSet;
    window.scrollTo({
      top: y,
      behavior: 'smooth',
    });
  };
  const handleReset = () => {
    onReset();
  };

  const handleChangeVehicleCategory = (e, value) => {
    changeHandlers.onChangeCategory({
      target: {
        value: value.split('*'),
      },
    });
  };

  return (
    <>
      <Box
        sx={{
          display: {
            xs: 'none',
            md: 'block',
          },
          flex: 1,
        }}
      >
        <Grid2 container spacing={3}>
          <FilterGroups
            changeHandlers={changeHandlers}
            expandedGroup={expandedGroup}
            filterData={filterData}
            onSelect={handleShowGroup}
            t={t}
          />
          <Grid2 size={{
            md: 'auto',
            xs: 12,
          }}
          >
            <Button
              color="dark"
              fullWidth
              onClick={handleReset}
              variant="outlined"
            >
              {t('components.vehiclefleet.SearchAndFilterBar.newSearch')}
            </Button>
          </Grid2>
          <Grid2 size={{
            md: 'auto',
            xs: 12,
          }}
          >
            <Button
              color="primary"
              fullWidth
              onClick={handleRefetch}
              variant="contained"
            >
              {totalCount}
              {' '}
              {t('components.vehiclefleet.SearchAndFilterBar.showVehicles')}
            </Button>
          </Grid2>
        </Grid2>
        <Collapse in={expandedGroup === 'basic'} timeout={animTimeout}>
          <FilterPanel spacing>
            <BasicFiltersGroup
              data={filterData}
              handlers={changeHandlers}
              t={t}
            />
          </FilterPanel>
        </Collapse>
        <Collapse in={expandedGroup === 'features'} timeout={animTimeout}>
          <FilterPanel spacing>
            <FeaturesFilterGroup
              data={filterData}
              handlers={changeHandlers}
              t={t}
            />
          </FilterPanel>
        </Collapse>
      </Box>
      <Box
        my={2}
        sx={{
          display: {
            xs: 'block',
            md: 'none',
          },
          position: 'relative',
          flex: 1,
          zIndex: 1,
          '& .MuiFormLabel-root.MuiInputLabel-outlined': {
            backgroundColor: theme.palette.primary.main,
          },
        }}
      >
        <Paper
          elevation={10}
          sx={{
            padding: theme.spacing(2),
            backgroundColor: theme.palette.primary.main,
          }}
        >
          <Grid2 container spacing={2} sx={{ alignItems: 'center' }}>
            <Grid2 size={{ xs: 12 }}>
              <VehicleCategory
                onChange={handleChangeVehicleCategory}
                t={t}
                vehicleCategories={filterData.categories}
                vehicleCategory={filterData.category}
              />
            </Grid2>
            <Grid2 size={{
              md: 6,
              sm: 6,
              xs: 6,
            }}
            >
              <Brand
                brand={filterData.brand}
                brands={filterData.brands}
                onChange={changeHandlers.onChangeBrand}
                t={t}
              />
            </Grid2>
            <Grid2 size={{
              md: 6,
              sm: 6,
              xs: 6,
            }}
            >
              <Model
                model={filterData.model}
                models={filterData.models}
                onChange={changeHandlers.onChangeModel}
                t={t}
              />
            </Grid2>
            <Grid2 size={{
              md: 3,
              sm: 12,
              xs: 12,
            }}
            >
              <Button
                color="secondary"
                component="a"
                endIcon={isDetailedSearchOpen ? <FiArrowUpCircle /> : <FiArrowDownCircle />}
                fullWidth
                onClick={() => setDetailedSearchOpen(!isDetailedSearchOpen)}
              >
                {t('components.organisms.CarSearchFilterBar.detailedSearch')}
              </Button>
            </Grid2>
            <Grid2 size={{
              sm: 12,
              xs: 12,
            }}
            >
              <Collapse in={isDetailedSearchOpen} timeout={animTimeout}>
                <Box sx={{ marginBottom: 2 }}>
                  <Grid2 container spacing={2}>
                    <Grid2 size={{
                      md: 12,
                      xs: 12,
                    }}
                    >
                      <FormSelect
                        id="fuel-type-select"
                        items={[
                          {
                            label: t('components.vehiclefleet.SearchAndFilterBar.BasicFiltersGroup.allFuels'),
                            value: null,
                          },
                          ...orderBy(filterData.fuelTypes.map((value) => ({
                            label: FUEL_TYPE[value],
                            value,
                          })), ['label']),
                        ]}
                        label={t('components.vehiclefleet.SearchAndFilterBar.BasicFiltersGroup.fuel')}
                        onChange={changeHandlers.onChangeFuelType}
                        value={filterData.fuelType}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                      <FormSelect
                        id="transmission-select"
                        items={[
                          {
                            label: t('components.vehiclefleet.SearchAndFilterBar.BasicFiltersGroup.allTransmissions'),
                            value: null,
                          },
                          ...orderBy(filterData.transmissions.map((value) => ({
                            label: TRANSMISSION_TYPE[value],
                            value,
                          })), ['label']),
                        ]}
                        label={t('components.vehiclefleet.SearchAndFilterBar.BasicFiltersGroup.transmission')}
                        onChange={changeHandlers.onChangeTransmission}
                        value={filterData.transmission}
                      />
                    </Grid2>
                    <Grid2 size={{
                      md: 4,
                      xs: 12,
                    }}
                    >
                      <FormSelect
                        id="branch-select"
                        items={[
                          {
                            label: t('components.vehiclefleet.SearchAndFilterBar.BasicFiltersGroup.allLocations'),
                            value: null,
                          },
                          ...orderBy(filterData.branches.map((value) => ({
                            label: value.attributes.name,
                            value: value.id,
                          })), ['label']),
                        ]}
                        label={t('components.vehiclefleet.SearchAndFilterBar.BasicFiltersGroup.location')}
                        onChange={changeHandlers.onChangeBranch}
                        value={filterData.branch}
                      />
                    </Grid2>
                  </Grid2>
                </Box>
                <Grid2 container spacing={5}>
                  <Grid2 size={{
                    md: 6,
                    xs: 12,
                  }}
                  >
                    <Typography color="secondary" component="div" variant="button">
                      {t('components.vehiclefleet.SearchAndFilterBar.BasicFiltersGroup.availabilityActions')}
                    </Typography>
                    <Grid2 container spacing={1}>
                      <Grid2 size={{
                        md: 6,
                        xs: 12,
                      }}
                      >
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={filterData.availableNow ?? false}
                              onChange={changeHandlers.onChangeAvailableNow}
                            />
                          )}
                          label={t('components.vehiclefleet.SearchAndFilterBar.BasicFiltersGroup.availableImmediately')}
                          sx={{
                            '& .MuiFormControlLabel-label.MuiTypography-body1': {
                              color: 'white',
                            },
                            '& .MuiSvgIcon-root': {
                              borderBottomColor: 'white',
                            },
                            '& .MuiCheckbox-root': {
                              color: 'white',
                            },
                          }}
                        />
                      </Grid2>
                      <Grid2 size={{
                        md: 6,
                        xs: 12,
                      }}
                      >
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={filterData.saleOnly ?? false}
                              onChange={changeHandlers.onChangeSaleOnly}
                            />
                          )}
                          label={t('components.vehiclefleet.SearchAndFilterBar.BasicFiltersGroup.vehiclesOnSale')}
                          sx={{
                            '& .MuiFormControlLabel-label.MuiTypography-body1': {
                              color: 'white',
                            },
                            '& .MuiSvgIcon-root': {
                              borderBottomColor: 'white',
                            },
                            '& .MuiCheckbox-root': {
                              color: 'white',
                            },
                          }}
                        />
                      </Grid2>
                    </Grid2>
                  </Grid2>
                </Grid2>
                <Grid2 container spacing={3}>
                  <Grid2 size={{
                    md: 6,
                    xs: 12,
                  }}
                  >
                    <Typography color="secondary" variant="button">
                      {t('components.vehiclefleet.SearchAndFilterBar.BasicFiltersGroup.typeOfVehicle')}
                    </Typography>
                    <Grid2 container spacing={1}>
                      <Grid2 size={{
                        md: 6,
                        xs: 12,
                      }}
                      >
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={filterData.newCar ?? false}
                              onChange={changeHandlers.onChangeNewCar}
                            />
                          )}
                          label={t('components.vehiclefleet.SearchAndFilterBar.BasicFiltersGroup.newCar')}
                          sx={{
                            '& .MuiFormControlLabel-label.MuiTypography-body1': {
                              color: 'white',
                            },
                            '& .MuiSvgIcon-root': {
                              borderBottomColor: 'white',
                            },
                            '& .MuiCheckbox-root': {
                              color: 'white',
                            },
                          }}
                        />
                      </Grid2>
                      <Grid2 size={{
                        md: 6,
                        xs: 12,
                      }}
                      >
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={filterData.usedCar ?? false}
                              onChange={changeHandlers.onChangeUsedCar}
                            />
                          )}
                          label={t('components.vehiclefleet.SearchAndFilterBar.BasicFiltersGroup.usedCars')}
                          sx={{
                            '& .MuiFormControlLabel-label.MuiTypography-body1': {
                              color: 'white',
                            },
                            '& .MuiSvgIcon-root': {
                              borderBottomColor: 'white',
                            },
                            '& .MuiCheckbox-root': {
                              color: 'white',
                            },
                          }}
                        />
                      </Grid2>
                      <Grid2 size={{
                        md: 6,
                        xs: 12,
                      }}
                      >
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={filterData.isDailyAdmission ?? false}
                              onChange={changeHandlers.onChangeIsDailyAdmission}
                            />
                          )}
                          label={t('components.vehiclefleet.SearchAndFilterBar.BasicFiltersGroup.dailyAdmissionCar')}
                          sx={{
                            '& .MuiFormControlLabel-label.MuiTypography-body1': {
                              color: 'white',
                            },
                            '& .MuiSvgIcon-root': {
                              borderBottomColor: 'white',
                            },
                            '& .MuiCheckbox-root': {
                              color: 'white',
                            },
                          }}
                        />
                      </Grid2>
                      <Grid2 size={{
                        md: 6,
                        xs: 12,
                      }}
                      >
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={filterData.seductionCar ?? false}
                              onChange={changeHandlers.onChangeSeductionCar}
                            />
                          )}
                          label={t('components.vehiclefleet.SearchAndFilterBar.BasicFiltersGroup.demonstrationCar')}
                          sx={{
                            '& .MuiFormControlLabel-label.MuiTypography-body1': {
                              color: 'white',
                            },
                            '& .MuiSvgIcon-root': {
                              borderBottomColor: 'white',
                            },
                            '& .MuiCheckbox-root': {
                              color: 'white',
                            },
                          }}
                        />
                      </Grid2>
                    </Grid2>
                  </Grid2>
                  <Grid2 size={{
                    md: 6,
                    xs: 12,
                  }}
                  >
                    <FormSlider
                      gutterBottom
                      id="horsePower"
                      label={t('components.vehiclefleet.SearchAndFilterBar.BasicFiltersGroup.performance')}
                      max={filterData.powerRange[1] ?? 1}
                      min={filterData.powerRange[0] ?? 0}
                      onChange={debounce(changeHandlers.onChangeHorsePower, 1000)}
                      sx={{
                        '& .MuiBox-root': {
                          color: 'white',
                        },
                        '& .MuiSlider-root': {
                          color: 'white',
                        },
                        '& .MuiTypography-root': {
                          color: 'white',
                        },
                        '& .MuiSlider-valueLabel span > span': {
                          color: 'black',
                        },
                      }}
                      unit="PS"
                      value={filterData.horsePower ?? filterData.powerRange ?? [0, 0]}
                    />
                    <FormSlider
                      gutterBottom
                      id="initialRegistrationYear"
                      label={t('components.vehiclefleet.SearchAndFilterBar.BasicFiltersGroup.initialRegistration')}
                      max={filterData.initialRegistrationYearRange[1] ?? 1}
                      min={filterData.initialRegistrationYearRange[0] ?? 0}
                      onChange={debounce(changeHandlers.onChangeInitialRegistrationYear, 1000)}
                      sx={{
                        '& .MuiBox-root': {
                          color: 'white',
                        },
                        '& .MuiSlider-root': {
                          color: 'white',
                        },
                        '& .MuiTypography-root': {
                          color: 'white',
                        },
                        '& .MuiSlider-valueLabel span > span': {
                          color: 'black',
                        },
                      }}
                      value={filterData.initialRegistrationYear ?? filterData.initialRegistrationYearRange ?? [0, 0]}
                    />
                    <FormSlider
                      gutterBottom
                      id="mileage"
                      label={t('components.vehiclefleet.SearchAndFilterBar.BasicFiltersGroup.kilometers')}
                      max={filterData.mileageRange[1] ?? 1}
                      min={filterData.mileageRange[0] ?? 0}
                      onChange={debounce(changeHandlers.onChangeMileage, 1000)}
                      sx={{
                        '& .MuiBox-root': {
                          color: 'white',
                        },
                        '& .MuiSlider-root': {
                          color: 'white',
                        },
                        '& .MuiTypography-root': {
                          color: 'white',
                        },
                        '& .MuiSlider-valueLabel span > span': {
                          color: 'black',
                        },
                      }}
                      unit="km"
                      value={filterData.mileage ?? filterData.mileageRange ?? [0, 0]}
                    />
                    <FormSlider
                      gutterBottom
                      id="price"
                      label={t('components.vehiclefleet.SearchAndFilterBar.BasicFiltersGroup.price')}
                      max={filterData.priceRange[1] ?? 1}
                      min={filterData.priceRange[0] ?? 0}
                      onChange={debounce(changeHandlers.onChangePrice, 1000)}
                      sx={{
                        '& .MuiBox-root': {
                          color: 'white',
                        },
                        '& .MuiSlider-root': {
                          color: 'white',
                        },
                        '& .MuiTypography-root': {
                          color: 'white',
                        },
                        '& .MuiSlider-valueLabel span > span': {
                          color: 'black',
                        },
                      }}
                      unit="€"
                      value={filterData.price ?? filterData.priceRange ?? [0, 0]}
                    />
                  </Grid2>
                </Grid2>
              </Collapse>
            </Grid2>
            <Grid2 size={{
              md: 'auto',
              xs: 12,
            }}
            >
              <Button
                color="secondary"
                fullWidth
                onClick={handleRefetch}
                variant="contained"
              >
                {totalCount}
                {' '}
                {t('components.vehiclefleet.SearchAndFilterBar.showVehicles')}
              </Button>
            </Grid2>
          </Grid2>
        </Paper>
      </Box>
    </>
  );
}

SearchAndFilterBar.propTypes = {
  filters: PropTypes.object,
  onChange: PropTypes.func,
  onReset: PropTypes.func,
};

function FilterGroups(props) {
  const {
    t,
    filterData,
    changeHandlers,
    expandedGroup,
    onSelect,
  } = props;
  const handlePressGroup = (group) => () => {
    onSelect(group);
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return FILTER_GROUPS
    .filter((filterGroup) => !(filterGroup.id === 'features' && isMobile))
    .map(({
      id,
      label,
      icon: Icon,
    }, index) => (
      <Grid2
        key={id}
        size={{
          md: 'auto',
          xs: 12,
        }}
        sx={{
          [theme.breakpoints.up('md')]: {
            flex: 1,
          },
        }}
      >
        <Select
          hideLabel
          id={`filter-group-${id}`}
          label={label}
          onOpen={handlePressGroup(id)}
          open={false}
          renderValue={() => (
            <Box alignItems="center" display="flex">
              <Icon
                style={{
                  marginRight: theme.spacing(1),
                }}
              />
              {t(`components.vehiclefleet.SearchAndFilterBar.${id}`)}
            </Box>
          )}
          size="small"
          sx={{
            '& .MuiSelect-outlined': {
              backgroundColor: 'white !important',
            },
            '& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-notchedOutline:hover': {
              borderColor: `${theme.palette.divider} !important`,
            },
            ...(expandedGroup === id && ({
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              '& .MuiSelect-select': {
                borderBottomLeftRadius: '0 !important',
                borderBottomRightRadius: '0 !important',
                color: theme.palette.text.primary,
              },
              '& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-notchedOutline:hover, & .MuiOutlinedInput-notchedOutline:focus': {
                borderColor: `${theme.palette.primary.main} !important`,
                borderBottomColor: 'white !important',
              },
              '& .MuiSelect-icon': {
                transform: 'rotate(180deg)',
                color: theme.palette.primary.main,
              },
            })),
          }}
          sxFormControl={{
            position: 'relative',
            '&::before, &::after': {
              content: '""',
              display: 'block',
              position: 'absolute',
              opacity: 0,
              width: '100%',
              backgroundColor: 'white',
              pointerEvents: 'none',
              transition: `opacity ${theme.transitions.duration.complex}ms ${theme.transitions.easing.easeInOut}`,
              ...(expandedGroup === id ? {
                opacity: 1,
              } : {}),
            },
            '&::before': {
              left: 0,
              bottom: -10,
              height: 20,
              zIndex: 1,
              borderLeft: `solid 2px ${theme.palette.primary.main}`,
              borderRight: `solid 2px ${theme.palette.primary.main}`,
            },
            '&::after': index === 0 ? {
              left: 0,
              bottom: -20,
              height: 20,
              zIndex: 2,
              borderLeft: `solid 2px ${theme.palette.primary.main}`,
              width: 20,
            } : {
              opacity: 0,
            },
          }}
          value="default"
        >
          <MenuItem value="default">
            {t(`components.vehiclefleet.SearchAndFilterBar.${id}`)}
          </MenuItem>
        </Select>
      </Grid2>
    ));
}

function FilterPanel({
  spacing,
  children,
}) {
  return (
    <Box mt={spacing ? 1 : 0}>
      <StyledFilterGroupContainer elevation={0}>
        <Box p={3}>
          {children}
        </Box>
      </StyledFilterGroupContainer>
    </Box>
  );
}

const StyledFilterGroupContainer = styled(Paper)(({ theme }) => ({
  zIndex: 2,
  borderStyle: 'solid',
  borderWidth: 2,
  borderColor: theme.palette.primary.main,
  [theme.breakpoints.down('sm')]: {
    borderTopWidth: 0,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
}));

FilterPanel.propTypes = {
  spacing: PropTypes.bool,
  children: PropTypes.any,
};

function FormSelect(props) {
  const {
    id,
    label,
    value,
    items,
    onChange,
  } = props;

  return (
    <Select
      color="secondary"
      label={label}
      onChange={onChange}
      value={value ?? ''}
    >
      {items.map(({
        label: label2,
        value: value2,
      }) => (
        <MenuItem key={value2} value={value2}>
          {label2}
        </MenuItem>
      ))}
    </Select>
  );
}

function Brand(props) {
  const {
    t,
    brands,
    brand,
    onChange,
  } = props;

  return (
    <Select
      color="secondary"
      label={t('components.organisms.CarSearchFilterBar.brand')}
      onChange={onChange}
      value={brand ?? ''}
    >
      <MenuItem key="empty" value={null}>
        {t('components.organisms.CarSearchFilterBar.allBrands')}
      </MenuItem>
      {brands.map((item) => (
        <MenuItem key={item} value={item}>
          {transformBrandTitle(item)}
        </MenuItem>
      ))}
    </Select>
  );
}

function Model(props) {
  const {
    t,
    models,
    model,
    onChange,
  } = props;

  return (
    <Select
      color="secondary"
      label={t('components.organisms.CarSearchFilterBar.model')}
      onChange={onChange}
      value={model ?? ''}
    >
      <MenuItem key="empty" value={null}>
        {t('components.organisms.CarSearchFilterBar.allModels')}
      </MenuItem>
      {models.map((item) => (
        <MenuItem key={item} value={item}>
          {item}
        </MenuItem>
      ))}
    </Select>
  );
}

function VehicleCategory(props) {
  const {
    vehicleCategory,
    onChange,
  } = props;

  return (
    <ButtonGroup
      color="secondary"
      fullWidth
      sx={{
        '& .MuiButton-outlined': {
          borderColor: (theme) => theme.palette.secondary.main,
        },
        '& .MuiButtonGroup-firstButton': {
          borderRightColor: 'transparent',
          borderTopLeftRadius: `${dimensions.defaultBorderRadius}px`,
          borderBottomLeftRadius: `${dimensions.defaultBorderRadius}px`,
        },
        '& .MuiButtonGroup-lastButton': {
          borderLeftColor: 'transparent',
          borderTopRightRadius: `${dimensions.defaultBorderRadius}px`,
          borderBottomRightRadius: `${dimensions.defaultBorderRadius}px`,
        },
      }}
      variant="outlined"
    >
      {VEHICLE_CATEGORIES.map((category) => (
        <Button
          key={category.name}
          aria-label={category.name}
          onClick={(event) => onChange(event, category.value.join('*') ?? null)}
          sx={{
            backgroundColor: isEqual(vehicleCategory, category.value)
              ? (theme) => theme.palette.secondary.main
              : 'transparent',
            color: isEqual(vehicleCategory, category.value)
              ? (theme) => theme.palette.primary.main
              : (theme) => theme.palette.secondary.main,
          }}
        >
          {category.icon}
        </Button>
      ))}
    </ButtonGroup>
  );
}

const VEHICLE_CATEGORIES = [
  {
    name: 'cars',
    icon: <FaCarSide size={30} />,
    value: ['compact', 'sedan', 'wagon', 'suv'],
  },
  {
    name: 'vans',
    icon: <FaVanShuttle size={30} />,
    value: ['van', 'bus'],
  },
  {
    name: 'trucks',
    icon: <FaTruck size={30} />,
    value: ['truck'],
  },
];

// const useStyles = makeStyles((theme) => ({
//   tabsContainer: {
//     borderWidth: 2,
//     borderColor: 'rgba(255,255,255,0.70)',
//     borderStyle: 'solid',
//     // borderRadius: theme.shape.borderRadius,
//     '& .MuiTabs-flexContainer': {
//       height: '100%',
//     },
//   },
//   tabsIndicator: {
//     display: 'none',
//   },
//   tabRoot: {
//     flex: 1,
//     minWidth: 0,
//     minHeight: 0,
//     // padding: theme.spacing(0.5),
//     borderRight: 2,
//     borderRightColor: 'white',
//     borderRightStyle: 'solid',
//     '&:last-child': {
//       borderRight: 0,
//     },
//   },
//   tabSelected: {
//     // borderColor: theme.palette.secondary.main,
//     backgroundColor: 'rgba(255,255,255,0.15)',
//     '& $tabWrapper': {
//       // color: theme.palette.secondary.main,
//     },
//   },
//   tabWrapper: {
//     flexDirection: 'column',
//     alignItems: 'center',
//     textTransform: 'none',
//     // color: theme.palette.primary.contrastText,
//     '& > img:first-child': {
//       // margin: `0 0 ${theme.spacing(0)}px 0 !important`,
//     },
//     '& > img path': {
//       fill: 'red',
//     },
//   },
// }));
